import React, { useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useLocation } from 'react-router-dom';
import { FiArrowLeft } from 'react-icons/fi';
import { MasonryPhotoAlbum } from 'react-photo-album';
import 'react-photo-album/masonry.css';
import Lightbox from 'yet-another-react-lightbox';
import Captions from 'yet-another-react-lightbox/plugins/captions';
import Zoom from 'yet-another-react-lightbox/plugins/zoom';
import 'yet-another-react-lightbox/styles.css';
import 'yet-another-react-lightbox/plugins/captions.css';

import { Navbar, Footer } from '../index';
import { countries, photos as allPhotos } from '../../galleryData';
import useInView from '../../useInView';
import {
  Page,
  MaxWidth,
  BackLink,
  Hero,
  Eyebrow,
  Title,
  Lead,
  Metrics,
  Metric,
  SectionLabel,
  FeaturedSection,
  FeaturedGrid,
  FeaturedCard,
  FeaturedOverlay,
  FeaturedBody,
  FeaturedFlag,
  FeaturedTitle,
  FilterBar,
  FilterChip,
  GridSection,
  EmptyState,
  PhotoBadge,
} from './GalleryPage.elements';

const fade = {
  hidden: { opacity: 0, y: 20 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, delay: 0.08 * i, ease: 'easeOut' },
  }),
};

function GalleryPage() {
  const { search } = useLocation();

  // /gallery?c=italy ile gelindiğinde o ülke filtresi seçili açılır
  const initialFilter = useMemo(() => {
    const slug = new URLSearchParams(search).get('c');
    return slug && countries.some((c) => c.slug === slug) ? slug : 'all';
  }, [search]);

  const [filter, setFilter] = useState(initialFilter);
  const [lightboxIndex, setLightboxIndex] = useState(-1);
  const [gridRef] = useInView({ amount: 0.05 });

  const filteredPhotos = useMemo(() => {
    if (filter === 'all') return allPhotos;
    return allPhotos.filter((p) => p.country === filter);
  }, [filter]);

  const flagOf = useMemo(() => {
    const map = {};
    countries.forEach((c) => {
      map[c.slug] = c.flag;
    });
    return map;
  }, []);

  // react-photo-album expects { src, width, height } shape.
  // srcSet ile küçük ekranda 600w thumb yeter — grid'de kalite kaybı olmaz.
  const albumPhotos = useMemo(
    () =>
      filteredPhotos.map((p) => ({
        src: p.src,
        width: p.width,
        height: p.height,
        alt: p.alt,
        city: p.city,
        flag: flagOf[p.country],
        srcSet: p.srcThumb
          ? [
              { src: p.srcThumb, width: 600, height: Math.round((600 * p.height) / p.width) },
              { src: p.src, width: 1200, height: Math.round((1200 * p.height) / p.width) },
            ]
          : undefined,
      })),
    [filteredPhotos, flagOf]
  );

  // Lightbox slides — native çözünürlük (srcFull), Zoom plugin anlamlı olsun
  const slides = useMemo(
    () =>
      filteredPhotos.map((p) => ({
        src: p.srcFull || p.src,
        width: p.width,
        height: p.height,
        alt: p.alt,
        title: p.city || p.alt,
        description: [p.city, countries.find((c) => c.slug === p.country)?.name]
          .filter(Boolean)
          .join(', '),
      })),
    [filteredPhotos]
  );

  const totalPhotos = allPhotos.length;

  return (
    <>
      <Helmet>
        <title>Travel Gallery — Enes Güngör</title>
        <meta
          name="description"
          content="A visual log of cities and countries from the last 6 months — photo gallery by Enes Güngör."
        />
        <link rel="canonical" href="https://enesgungor.com/gallery" />
      </Helmet>
      <Navbar toggle={() => {}} />
      <Page>
        <MaxWidth>
          <BackLink to="/">
            <FiArrowLeft /> Back to portfolio
          </BackLink>

          <Hero>
            <Eyebrow variants={fade} initial="hidden" animate="visible">
              Travel Gallery · 2026
            </Eyebrow>
            <Title variants={fade} custom={1} initial="hidden" animate="visible">
              Six months <em>in motion</em>, frame by frame.
            </Title>
            <Lead>
              When I&apos;m not shipping product, I&apos;m chasing light in new
              cities. A visual diary of the places that shaped the first half of
              2026 — from the Arctic circle in March to the Alps in July, shot on
              iPhone and edited in between trains.
            </Lead>
            <Metrics>
              <Metric>
                <strong>{countries.length}</strong>
                <span>Countries</span>
              </Metric>
              <Metric>
                <strong>
                  {countries.reduce((s, c) => s + c.cities.length, 0)}
                </strong>
                <span>Cities</span>
              </Metric>
              <Metric>
                <strong>{totalPhotos}+</strong>
                <span>Photos</span>
              </Metric>
            </Metrics>
          </Hero>

          <FeaturedSection>
            <SectionLabel>
              <span>Featured moments</span>
            </SectionLabel>
            <FeaturedGrid>
              {countries.map((c, i) => {
                if (!c.featured) return null;
                return (
                  <FeaturedCard
                    key={c.slug}
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.05 * i }}
                    onClick={() => {
                      // featured fotosu set'in başına alalım — lightbox o ülkeden başlasın
                      setFilter(c.slug);
                      // filtre değişince index reset; bir tick sonra aç
                      setTimeout(() => setLightboxIndex(0), 50);
                    }}
                    aria-label={`Open ${c.name} gallery`}
                  >
                    <img
                      src={c.featured.srcThumb || c.featured.src}
                      srcSet={
                        c.featured.srcThumb
                          ? `${c.featured.srcThumb} 600w, ${c.featured.src} 1200w`
                          : undefined
                      }
                      sizes="(max-width: 700px) 92vw, 420px"
                      alt={c.featured.caption}
                      loading={i < 2 ? undefined : 'lazy'}
                    />
                    <FeaturedOverlay />
                    <FeaturedBody>
                      <FeaturedFlag>
                        <span aria-hidden="true">{c.flag}</span>
                        {c.name}
                      </FeaturedFlag>
                      <FeaturedTitle>{c.featured.caption}</FeaturedTitle>
                    </FeaturedBody>
                  </FeaturedCard>
                );
              })}
            </FeaturedGrid>
          </FeaturedSection>

          <GridSection>
            <SectionLabel>
              <span>All photos</span>
            </SectionLabel>

            <FilterBar>
              <FilterChip
                $active={filter === 'all'}
                onClick={() => setFilter('all')}
              >
                All · {allPhotos.length}
              </FilterChip>
              {countries.map((c) => {
                const count = allPhotos.filter(
                  (p) => p.country === c.slug
                ).length;
                return (
                  <FilterChip
                    key={c.slug}
                    $active={filter === c.slug}
                    onClick={() => setFilter(c.slug)}
                  >
                    <span aria-hidden="true">{c.flag}</span> {c.name} · {count}
                  </FilterChip>
                );
              })}
            </FilterBar>

            {albumPhotos.length === 0 ? (
              <EmptyState>No photos here yet.</EmptyState>
            ) : (
              <div ref={gridRef}>
                <MasonryPhotoAlbum
                  photos={albumPhotos}
                  columns={(containerWidth) => {
                    if (containerWidth < 500) return 2;
                    if (containerWidth < 900) return 3;
                    return 4;
                  }}
                  spacing={14}
                  onClick={({ index }) => setLightboxIndex(index)}
                  render={{
                    extras: (_, { photo }) =>
                      photo.city ? (
                        <PhotoBadge>
                          <span aria-hidden="true">{photo.flag}</span>
                          {photo.city}
                        </PhotoBadge>
                      ) : null,
                  }}
                />
              </div>
            )}
          </GridSection>
        </MaxWidth>

        <Lightbox
          open={lightboxIndex >= 0}
          index={lightboxIndex}
          close={() => setLightboxIndex(-1)}
          slides={slides}
          plugins={[Captions, Zoom]}
          captions={{ descriptionTextAlign: 'center' }}
          carousel={{ finite: false }}
          animation={{ fade: 300 }}
        />
      </Page>
      <Footer />
    </>
  );
}

export default GalleryPage;
